import { apiClient } from './axiosInstance';
import type { EncounterFieldsConfig } from '../constants/encounterFieldsConfig';

/** Response from GET /api/v1/tenants/{tenantId}/settings */
export interface TenantSettingsResponse {
  claimTypes: string[];
  /** Feature 043. `{}` when the practice has never been configured. */
  encounterFieldsConfig: EncounterFieldsConfig;
}

/** Request body for PUT /api/v1/tenants/{tenantId}/settings */
export interface UpdateSettingsRequest {
  claimTypes?: string[];
  /** Feature 043. Omitted leaves the stored config unchanged. */
  encounterFieldsConfig?: EncounterFieldsConfig;
}

/** Response row from GET /api/v1/tenants (feature 043 — backoffice practices list) */
export interface TenantSummaryResponse {
  tenantId: string;
  directory: string;
  displayName: string | null;
  status: string;
  claimTypes: string[];
}

/** Fields read from GET /api/v1/tenants/{tenantId} (feature 043 — practice settings title). */
export interface TenantIdentity {
  directory: string;
  displayName: string | null;
}

const EMPTY_ENCOUNTER_FIELDS_CONFIG: EncounterFieldsConfig = { encounterFields: {}, claimTypeFields: {} };

/**
 * Get tenant settings by tenant ID.
 * Returns an empty claimTypes array / encounterFieldsConfig if no settings exist.
 */
export async function getTenantSettings(
  tenantId: string
): Promise<TenantSettingsResponse> {
  const response = await apiClient.get<TenantSettingsResponse>(
    `/api/v1/tenants/${tenantId}/settings`
  );
  return {
    ...response.data,
    claimTypes: response.data.claimTypes ?? [],
    encounterFieldsConfig: response.data.encounterFieldsConfig ?? EMPTY_ENCOUNTER_FIELDS_CONFIG,
  };
}

/**
 * Update tenant settings.
 * Uses upsert pattern - creates if not exists.
 */
export async function updateTenantSettings(
  tenantId: string,
  request: UpdateSettingsRequest
): Promise<TenantSettingsResponse> {
  const response = await apiClient.put<TenantSettingsResponse>(
    `/api/v1/tenants/${tenantId}/settings`,
    request
  );
  return response.data;
}

/** List all practices (cross-tenant, backoffice-only). Feature 043. */
export async function listTenants(): Promise<TenantSummaryResponse[]> {
  const response = await apiClient.get<TenantSummaryResponse[]>('/api/v1/tenants');
  return response.data;
}

/** Fetch a single tenant's directory/displayName (feature 043 — practice settings page title). */
export async function getTenant(tenantId: string): Promise<TenantIdentity> {
  const response = await apiClient.get<TenantIdentity>(`/api/v1/tenants/${tenantId}`);
  return response.data;
}
